import { useEffect, useState } from "react";
import axios from "axios";
import { LogoMark } from "@/components/Logo";
import { Lock, Loader2, RefreshCw, LogOut, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [leads, setLeads] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async (k) => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/leads`, { headers: { "x-admin-key": k } });
      setLeads(res.data);
      sessionStorage.setItem("mss_admin_key", k);
    } catch (e) {
      setLeads(null);
      sessionStorage.removeItem("mss_admin_key");
      setErr(e?.response?.status === 401 ? "Wrong passcode. Please try again." : "Could not load enquiries. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem("mss_admin_key");
    if (saved) load(saved);
  }, []);

  const logout = () => {
    sessionStorage.removeItem("mss_admin_key");
    setLeads(null);
    setKeyInput("");
    setErr("");
  };

  if (!leads) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian px-5">
        <div className="w-full max-w-md rounded-[1.4rem] border border-gold/20 bg-surface p-9">
          <div className="flex items-center gap-3">
            <LogoMark className="h-11 w-11" />
            <div className="leading-none">
              <p className="font-display text-lg font-bold text-warm">Maa Sakumbri</p>
              <p className="mt-1.5 font-mono text-[9px] tracking-[0.42em] text-gold">SOLAR — ADMIN</p>
            </div>
          </div>
          <h1 className="mt-8 font-display text-2xl font-bold text-warm">Leads Dashboard</h1>
          <p className="mt-2 text-sm text-mutegray">Enter your admin passcode to view website enquiries.</p>
          <input
            type="password"
            className="field mt-6"
            placeholder="Admin passcode"
            value={keyInput}
            onChange={(e) => { setKeyInput(e.target.value); setErr(""); }}
            onKeyDown={(e) => e.key === "Enter" && keyInput && load(keyInput)}
            data-testid="admin-passcode-input"
          />
          {err && <p className="mt-2 text-xs text-red-400" data-testid="admin-error">{err}</p>}
          <button
            type="button"
            onClick={() => keyInput && load(keyInput)}
            disabled={loading || !keyInput}
            data-testid="admin-unlock-btn"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-bold text-ink transition-all duration-300 hover:bg-goldlight disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {loading ? "Checking…" : "Unlock dashboard"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian px-5 py-10 sm:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1300px]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoMark className="h-10 w-10" />
            <div className="leading-none">
              <p className="font-display text-lg font-bold text-warm">Leads Dashboard</p>
              <p className="mt-1 font-mono text-[9px] tracking-[0.3em] text-gold">{leads.length} ENQUIRIES</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => load(sessionStorage.getItem("mss_admin_key"))}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-[13px] font-semibold text-warm transition-colors duration-300 hover:border-gold/50 hover:text-gold"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-[13px] font-semibold text-warm transition-colors duration-300 hover:border-red-400/60 hover:text-red-400"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        {leads.length === 0 ? (
          <div className="mt-16 flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 py-20 text-center">
            <Inbox className="h-10 w-10 text-gold/60" />
            <p className="mt-4 text-sm text-mutegray">No enquiries yet — they will appear here the moment someone submits the quote form.</p>
          </div>
        ) : (
          <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-leads-table">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-surface font-mono text-[10px] uppercase tracking-[0.18em] text-gold">
                  {["Date", "Name", "Phone", "Email", "City", "Property", "Interested in", "Bill", "Message"].map((h) => (
                    <th key={h} className="px-4 py-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 text-warm/85 transition-colors hover:bg-white/[0.03]" data-testid="admin-lead-row">
                    <td className="px-4 py-4 text-xs text-mutegray">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-4 font-semibold text-warm">{l.name}</td>
                    <td className="px-4 py-4">{l.phone}</td>
                    <td className="px-4 py-4 text-xs">{l.email}</td>
                    <td className="px-4 py-4">{l.city || "—"}</td>
                    <td className="px-4 py-4 text-xs">{l.property_type}</td>
                    <td className="px-4 py-4 text-xs">{l.product_interest}</td>
                    <td className="px-4 py-4 text-xs">{l.monthly_bill || "—"}</td>
                    <td className="max-w-[260px] px-4 py-4 text-xs text-mutegray">{l.message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
